/**
 * Flamethrower for Fire cards (CardFx): a WebGL fragment shader draws a jet of fire from the card's left
 * edge — turbulent flame tongues (fractal noise flowing along the jet), a white-hot core, orange and
 * dark-red edges — that shoots out, burns and dies in FLAME_SECONDS. Premultiplied alpha, so it reads
 * on dark and light art alike.
 */
export const FLAME_SECONDS = 1.4;

const VS = `attribute vec2 p; varying vec2 uv; void main(){ uv = p * .5 + .5; gl_Position = vec4(p, 0., 1.); }`;

const FS = `precision highp float;
varying vec2 uv; uniform float t; uniform float aspect;
float hash(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p){ vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3. - 2. * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y); }
float fbm(vec2 p){ float v = 0., a = .5; for (int k = 0; k < 5; k++) { v += a * noise(p); p = p * 2.03 + vec2(1.7, 9.2); a *= .5; } return v; }
void main(){
  // Jet from the left edge, slightly below the middle, along +x.
  float x = uv.x, y = (uv.y - .5) / aspect;
  float grow = smoothstep(0., .28, t);                    // the jet shoots out
  float die = 1. - smoothstep(.75, 1.35, t);              // and burns out
  float len = 1.25 * grow;
  float s = x / max(len, .001);                           // 0 at the nozzle, 1 at the tip
  float r = .05 + .3 * s * (1. + .25 * s);                                // the cone widens
  // Turbulence flowing along the jet.
  vec2 q = vec2(x * 3. - t * 7., y * 4.5);
  float n = fbm(q + fbm(q * 1.7 + vec2(t * 2., 0.)) * 1.3);
  float wob = (n - .5) * .34 * s;                         // the flame tongues
  float d = abs(y + wob) / r;
  float body = 1. - smoothstep(.0, 1., d);
  float edge = fbm(q * 2.3 - vec2(t * 3., 0.));
  float flame = body * (.5 + 1.0 * n) * (.75 + .5 * edge) * smoothstep(1.1, .5, s) * step(0., x);
  flame *= die * (.8 + .2 * grow);
  float T = clamp(flame * 1.35, 0., 1.);
  vec3 col = mix(vec3(.35, .04, .01), vec3(.95, .25, .02), smoothstep(.0, .35, T));
  col = mix(col, vec3(1., .62, .08), smoothstep(.3, .6, T));
  col = mix(col, vec3(1., .9, .45), smoothstep(.6, .85, T));
  col = mix(col, vec3(1., .99, .9), smoothstep(.85, 1., T));
  float a = smoothstep(.06, .4, flame);
  gl_FragColor = vec4(col * a, a);                        // premultiplied
}`;

export interface FlameRenderer {
  /** Draws the frame `t` seconds after the attack started. */
  draw: (t: number) => void;
  /** Frees the WebGL context (browsers allow only a few at once). */
  dispose: () => void;
}

/** null when WebGL is unavailable (the effect is then just skipped). */
export function createFlameRenderer(canvas: HTMLCanvasElement): FlameRenderer | null {
  const gl = canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: false });
  if (!gl) return null;
  const shader = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  };
  const vs = shader(gl.VERTEX_SHADER, VS);
  const fs = shader(gl.FRAGMENT_SHADER, FS);
  const program = gl.createProgram();
  if (!vs || !fs || !program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(program, "p");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const uT = gl.getUniformLocation(program, "t");
  const uAspect = gl.getUniformLocation(program, "aspect");

  return {
    draw(t) {
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform1f(uT, t);
      gl.uniform1f(uAspect, canvas.width / canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
    dispose() {
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    },
  };
}
